import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiAlertCircle, FiCheck, FiCopy, FiRotateCcw } from "react-icons/fi";
import useCallCenterT from "../../hooks/useCallCenterT";
import AppContext from "../../context/AppContext";
import { getSlaConfig, getSlaConfigMetadata, saveSlaConfig } from "../../service/CallCenterAlerts";
import { isPermissionDeniedError } from "../../utils/authSession";

const translateMetadataValue = (t, prefix, code, fallback) => {
  if (!code) return fallback;
  const key = `${prefix}_${code}`;
  const translated = t(key);
  return translated !== key ? translated : fallback;
};

const localizeSlaMetadata = (metadata, t) => {
  if (!metadata) return null;
  return {
    ...metadata,
    itemTypes: (metadata.itemTypes || []).map((item) => ({
      ...item,
      label: translateMetadataValue(t, "ccSlaItemType", item.code, item.label),
      description: translateMetadataValue(t, "ccSlaItemDesc", item.code, item.description),
    })),
    thresholdFields: (metadata.thresholdFields || []).map((field) => ({
      ...field,
      label: translateMetadataValue(t, "ccSlaField", field.key, field.label),
      description: translateMetadataValue(t, "ccSlaFieldDesc", field.key, field.description),
    })),
    timeUnits: (metadata.timeUnits || []).map((unit) => ({
      ...unit,
      label: translateMetadataValue(t, "ccSlaUnitLabel", unit.code, unit.label),
    })),
  };
};

const DEFAULT_THRESHOLDS = {
  warning: { value: 5, unit: "MINUTES" },
  escalationL1: { value: 10, unit: "MINUTES" },
  escalationL2: { value: 20, unit: "MINUTES" },
  acknowledgementGrace: { value: 5, unit: "MINUTES" },
};

const INPUT_CLASS =
  "h-9 w-[4.25rem] rounded-lg border border-slate-200 bg-white px-2 text-sm font-medium text-slate-800 text-center tabular-nums outline-none transition focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15 disabled:bg-slate-50 disabled:text-slate-400";

const SELECT_CLASS =
  "h-9 min-w-[6.5rem] rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 outline-none transition focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15 disabled:bg-slate-50 disabled:text-slate-400";

const toMinutes = (threshold) => {
  if (!threshold) return 0;
  const value = Number(threshold.value || 0);
  switch (threshold.unit) {
    case "HOURS":
      return value * 60;
    case "DAYS":
      return value * 24 * 60;
    default:
      return value;
  }
};

const formatDurationLabel = (minutes, t) => {
  if (minutes <= 0) return t("ccSlaImmediate");
  if (minutes >= 24 * 60 && minutes % (24 * 60) === 0) {
    const days = minutes / (24 * 60);
    return `${days}${t("ccSlaUnitDayShort")}`;
  }
  if (minutes >= 60 && minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours}${t("ccSlaUnitHourShort")}`;
  }
  return `${minutes}${t("ccSlaUnitMinuteShort")}`;
};

const cloneThreshold = (threshold) => ({
  value: Number(threshold?.value ?? 0),
  unit: threshold?.unit || "MINUTES",
});

const cloneConfig = (config) => ({
  ...config,
  warning: cloneThreshold(config.warning),
  escalationL1: cloneThreshold(config.escalationL1),
  escalationL2: cloneThreshold(config.escalationL2),
  acknowledgementGrace: cloneThreshold(config.acknowledgementGrace),
});

const Toggle = ({ checked, disabled, onChange, label, isRtl = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
      checked ? "bg-[#2B7FFF]" : "bg-slate-300"
    } disabled:opacity-50`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
        checked
          ? isRtl
            ? "-translate-x-[1.35rem]"
            : "translate-x-[1.35rem]"
          : "translate-x-0.5"
      }`}
    />
  </button>
);

const SlaTimeline = ({ config, thresholdFields, t }) => {
  const steps = useMemo(() => {
    if (!config?.active) return [];
    const warning = toMinutes(config.warning);
    const l1 = toMinutes(config.escalationL1);
    const l2 = toMinutes(config.escalationL2);
    const ack = toMinutes(config.acknowledgementGrace);
    const labelFor = (key, fallback) =>
      thresholdFields.find((field) => field.key === key)?.label || fallback;
    return [
      { key: "pending", label: t("ccSlaStepPending"), at: 0, tone: "slate" },
      { key: "warning", label: labelFor("warning", "Warning"), at: warning, tone: "amber" },
      { key: "l1", label: labelFor("escalationL1", "L1"), at: l1, tone: "orange" },
      {
        key: "l2",
        label: labelFor("escalationL2", "L2"),
        at: l2,
        tone: "red",
        note: ack > 0 ? t("ccSlaAckGraceNote").replace("{duration}", formatDurationLabel(ack, t)) : null,
      },
    ];
  }, [config, thresholdFields, t]);

  if (!steps.length) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2.5 text-xs text-slate-500">
        <FiAlertCircle className="shrink-0" aria-hidden="true" />
        {t("ccSlaDisabledTimeline")}
      </div>
    );
  }

  const toneClasses = {
    slate: "border-slate-200 bg-slate-50 text-slate-600",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    orange: "border-orange-200 bg-orange-50 text-orange-700",
    red: "border-red-200 bg-red-50 text-red-700",
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {t("ccSlaEscalationPreview")}
      </p>
      <div className="flex items-start">
        {steps.map((step, index) => (
          <React.Fragment key={step.key}>
            <div className="flex min-w-0 flex-1 flex-col items-center text-center">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-[11px] font-bold ${toneClasses[step.tone]}`}
              >
                {index + 1}
              </div>
              <p className="mt-2 text-xs font-medium text-slate-700">{step.label}</p>
              <p className="mt-0.5 text-xs font-semibold text-slate-900">{formatDurationLabel(step.at, t)}</p>
              {step.note ? <p className="mt-1 max-w-[8rem] text-[10px] leading-tight text-slate-400">{step.note}</p> : null}
            </div>
            {index < steps.length - 1 ? (
              <div className="mx-1 mt-4 h-px min-w-[1.5rem] flex-1 bg-slate-300" aria-hidden="true" />
            ) : null}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const AgentSlaSettings = ({ canEdit = false }) => {
  const t = useCallCenterT();
  const { langMode } = useContext(AppContext);
  const isRtl = langMode === "ar";
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [slaConfigs, setSlaConfigs] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  const localizedMetadata = useMemo(() => localizeSlaMetadata(metadata, t), [metadata, t]);

  const itemTypeMeta = useMemo(() => {
    const map = new Map();
    (localizedMetadata?.itemTypes || []).forEach((item) => map.set(item.code, item));
    return map;
  }, [localizedMetadata]);

  const thresholdFields = localizedMetadata?.thresholdFields || [];
  const timeUnits = localizedMetadata?.timeUnits || [
    { code: "MINUTES", label: t("ccSlaUnitLabel_MINUTES"), maxValue: 525600 },
    { code: "HOURS", label: t("ccSlaUnitLabel_HOURS"), maxValue: 8760 },
    { code: "DAYS", label: t("ccSlaUnitLabel_DAYS"), maxValue: 365 },
  ];

  const activeConfig = slaConfigs.find((row) => row.itemType === activeTab);
  const otherTypes = slaConfigs.filter((row) => row.itemType !== activeTab);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [configs, meta] = await Promise.all([getSlaConfig(), getSlaConfigMetadata()]);
      setMetadata(meta);
      const rows = Array.isArray(configs) ? configs.map(cloneConfig) : [];
      setSlaConfigs(rows);
      setActiveTab((current) => current || rows[0]?.itemType || null);
    } catch (error) {
      if (!isPermissionDeniedError(error)) {
        const message = error?.message || t("ccSlaSettingsLoadFailed");
        setLoadError(message);
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const updateConfig = (itemType, updater) => {
    setSlaConfigs((prev) =>
      prev.map((row) => (row.itemType === itemType ? updater({ ...row }) : row))
    );
  };

  const updateThreshold = (itemType, fieldKey, patch) => {
    updateConfig(itemType, (row) => ({
      ...row,
      [fieldKey]: { ...row[fieldKey], ...patch },
    }));
  };

  const copyFromType = (targetType, sourceType) => {
    if (!sourceType || targetType === sourceType) return;
    const source = slaConfigs.find((row) => row.itemType === sourceType);
    if (!source) return;
    updateConfig(targetType, (row) => ({
      ...row,
      warning: cloneThreshold(source.warning),
      escalationL1: cloneThreshold(source.escalationL1),
      escalationL2: cloneThreshold(source.escalationL2),
      acknowledgementGrace: cloneThreshold(source.acknowledgementGrace),
    }));
    toast.info(t("ccSlaCopiedFrom").replace("{source}", itemTypeMeta.get(sourceType)?.label || sourceType));
  };

  const resetType = (itemType) => {
    updateConfig(itemType, (row) => ({
      ...row,
      ...DEFAULT_THRESHOLDS,
      active: true,
    }));
    toast.info(t("ccSlaResetToDefaults"));
  };

  const maxValueForUnit = (unitCode) =>
    timeUnits.find((unit) => unit.code === unitCode)?.maxValue || 525600;

  const handleSave = async () => {
    if (!canEdit) return;
    try {
      setSaving(true);
      await saveSlaConfig(slaConfigs);
      toast.success(t("ccSlaSettingsSaved"));
      load();
    } catch (error) {
      toast.error(error.message || t("ccSlaSettingsSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-10 rounded-lg bg-slate-100" />
        <div className="h-48 rounded-xl bg-slate-100" />
        <div className="h-24 rounded-xl bg-slate-100" />
      </div>
    );
  }

  if (loadError && slaConfigs.length === 0) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <p>{loadError}</p>
        <button
          type="button"
          onClick={load}
          className="mt-3 rounded-lg border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
        >
          {t("ccRefresh")}
        </button>
      </div>
    );
  }

  const activeMeta = activeConfig ? itemTypeMeta.get(activeConfig.itemType) : null;

  return (
    <div className="flex flex-col">
      {loadError ? (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {loadError}
        </div>
      ) : null}

      <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50/60 px-3.5 py-2.5 text-sm text-blue-900">
        {t("ccSlaPriorityHint")}
      </div>

      {/* Type tabs */}
      <div className="mb-0 flex flex-wrap gap-1 border-b border-slate-200">
        {slaConfigs.map((config) => {
          const meta = itemTypeMeta.get(config.itemType);
          const isActive = activeTab === config.itemType;
          return (
            <button
              key={config.itemType}
              type="button"
              onClick={() => setActiveTab(config.itemType)}
              className={`relative inline-flex items-center gap-2 rounded-t-lg px-4 py-2.5 text-sm font-medium transition ${
                isActive
                  ? "bg-white text-[#2B7FFF] shadow-[inset_0_-2px_0_0_#2B7FFF]"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              {meta?.label || config.itemType}
              <span
                className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                  config.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                }`}
              >
                {config.active ? t("ccSlaStatusOn") : t("ccSlaStatusOff")}
              </span>
            </button>
          );
        })}
      </div>

      {activeConfig ? (
        <div className="rounded-b-xl border border-t-0 border-slate-200 bg-white">
          {/* Type header */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-slate-900">{activeMeta?.label || activeConfig.itemType}</h3>
              {activeMeta?.description ? (
                <p className="mt-0.5 text-sm text-slate-500">{activeMeta.description}</p>
              ) : null}
            </div>
            <div className={`flex shrink-0 items-center gap-3 ${isRtl ? "flex-row-reverse" : ""}`}>
              <span className="text-sm text-slate-600">{t("ccSlaEnabled")}</span>
              <Toggle
                checked={activeConfig.active}
                disabled={!canEdit}
                isRtl={isRtl}
                label={t("ccSlaEnabled")}
                onChange={() =>
                  updateConfig(activeConfig.itemType, (row) => ({ ...row, active: !row.active }))
                }
              />
            </div>
          </div>

          {/* Threshold table */}
          <div className={`px-5 py-4 ${activeConfig.active ? "" : "opacity-50"}`}>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-start">
                  <th className="pb-2 pe-4 text-xs font-semibold uppercase tracking-wide text-slate-400">
                    {t("ccSlaThresholdColumn")}
                  </th>
                  <th className="hidden pb-2 pe-4 text-xs font-semibold uppercase tracking-wide text-slate-400 sm:table-cell">
                    {t("ccSlaDescriptionColumn")}
                  </th>
                  <th className="pb-2 text-end text-xs font-semibold uppercase tracking-wide text-slate-400">
                    {t("ccSlaDurationColumn")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {thresholdFields.map((field) => {
                  const threshold = activeConfig[field.key] || { value: 0, unit: "MINUTES" };
                  const minValue = field.allowZero ? 0 : field.minValue;
                  const disabled = !canEdit || !activeConfig.active;
                  return (
                    <tr key={field.key} className="border-b border-slate-50 last:border-0">
                      <td className="py-3 pe-4 align-middle">
                        <span className="font-medium text-slate-800">{field.label}</span>
                        {field.description ? (
                          <p className="mt-0.5 text-xs text-slate-400 sm:hidden">{field.description}</p>
                        ) : null}
                      </td>
                      <td className="hidden py-3 pe-4 align-middle text-xs text-slate-500 sm:table-cell">
                        {field.description || "—"}
                      </td>
                      <td className="py-3 align-middle">
                        <div className="flex items-center justify-end gap-2" dir="ltr">
                          <input
                            type="number"
                            min={minValue}
                            max={maxValueForUnit(threshold.unit)}
                            disabled={disabled}
                            value={threshold.value ?? ""}
                            onChange={(e) =>
                              updateThreshold(activeConfig.itemType, field.key, {
                                value: Number(e.target.value),
                              })
                            }
                            className={INPUT_CLASS}
                            aria-label={`${activeMeta?.label || activeConfig.itemType} ${field.label}`}
                          />
                          <select
                            disabled={disabled}
                            value={threshold.unit || "MINUTES"}
                            onChange={(e) =>
                              updateThreshold(activeConfig.itemType, field.key, { unit: e.target.value })
                            }
                            className={SELECT_CLASS}
                            aria-label={`${activeMeta?.label || activeConfig.itemType} ${field.label} unit`}
                          >
                            {timeUnits.map((unit) => (
                              <option key={unit.code} value={unit.code}>
                                {unit.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Timeline */}
          <div className="border-t border-slate-100 px-5 py-4">
            <SlaTimeline config={activeConfig} thresholdFields={thresholdFields} t={t} />
          </div>

          {/* Row actions */}
          {canEdit ? (
            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 bg-slate-50/50 px-5 py-3">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <FiCopy className="text-slate-400" aria-hidden="true" />
                <span>{t("ccSlaCopyFrom")}</span>
                <select
                  defaultValue=""
                  onChange={(e) => {
                    copyFromType(activeConfig.itemType, e.target.value);
                    e.target.value = "";
                  }}
                  className={`${SELECT_CLASS} min-w-[9rem] text-sm`}
                >
                  <option value="">{t("ccSlaCopyFromPlaceholder")}</option>
                  {otherTypes.map((row) => (
                    <option key={row.itemType} value={row.itemType}>
                      {itemTypeMeta.get(row.itemType)?.label || row.itemType}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() => resetType(activeConfig.itemType)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                <FiRotateCcw className="text-slate-400" aria-hidden="true" />
                {t("ccSlaResetDefaults")}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Save — exposed for page sticky bar via form id, kept here for standalone use */}
      {canEdit ? (
        <div className="mt-6 flex items-center justify-end gap-3 border-t border-slate-100 pt-5">
          <p className="me-auto hidden text-xs text-slate-400 sm:block">{t("ccSlaSaveHint")}</p>
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#2B7FFF] px-5 text-sm font-medium text-white transition hover:bg-[#1f6fe8] disabled:opacity-60"
          >
            {saving ? t("ccSaving") : (
              <>
                <FiCheck aria-hidden="true" />
                {t("ccSaveSlaSettings")}
              </>
            )}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default AgentSlaSettings;
